import type { Header } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import * as React from 'react';

import { ColorHex, type ColorPalette } from '@acceligentllc/shared/enums/color';

import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';

type Props<T> = {
	setHeaderReference: (headerId: string) => (_ref: Nullable<HTMLElement>) => void;
	header: Header<T, unknown>;
	onColumnDrag: (header: string) => (e: React.MouseEvent<Element, MouseEvent>) => void;
	isLeafHeader?: boolean;
};

function BaseHeaderCell<T>(props: React.PropsWithChildren<Props<T>>) {
	const { header, onColumnDrag, setHeaderReference, isLeafHeader = true } = props;

	const isDisplayColumn = !header.column.accessorFn;
	const meta = header.column.columnDef.meta as unknown as { color?: ColorPalette; } | undefined;
	const color = meta?.color;
	const isGroupHeader = !header.isPlaceholder && header.subHeaders?.length > 0;

	const headerContent = React.useMemo(() => {
		return flexRender(header.column.columnDef.header, header.getContext());
	}, [header]);

	return (
		<div
			className={[
				styles['simple-table__header-cell'],
				isLeafHeader ? styles['simple-table__header-cell--leaf'] : '',
				isGroupHeader && color ? styles['simple-table__header-cell--group'] : '',
			].join(' ')}
			key={header.id}
			ref={setHeaderReference(header.id)}
			style={isGroupHeader && color ? { backgroundColor: `${ColorHex[color] ?? color}` } : undefined}
		>
			<div className={styles['simple-table__header-cell__content']}>
				<div className={styles['simple-table__header-cell__name-with-icon']}>
					<Tooltip message={<>{headerContent}</>}>
						<div className={styles['simple-table__header-cell__name']}>
							{headerContent}
						</div>
					</Tooltip>
				</div>
			</div>
			{!isDisplayColumn && (
				<div
					className={styles['simple-table__resizer']}
					onMouseDown={onColumnDrag(header.id)}
				/>
			)}
		</div>
	);
}

export default React.memo(BaseHeaderCell) as typeof BaseHeaderCell;
